import React from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Formik } from "formik";
import { resetPassword } from "../../redux/actions/authActions";
import AuthLayout from "../../layouts/Auth";
import { getApiErrorMessage } from "../../utils/apiError";
import { useTranslation } from "react-i18next";

import {
  Button,
  CircularProgress,
  Paper,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { Alert as MuiAlert } from "@material-ui/lab";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Wrapper = styled(Paper)`
  width: 100%;
  max-width: 520px;
  padding: ${(props) => props.theme.spacing(4)}px;
  border-radius: 24px;
  box-shadow: 0 24px 56px rgba(15, 23, 42, 0.12);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(6)}px;
  }
`;

function ResetPassword() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [requestSent, setRequestSent] = React.useState(false);

  return (
    <AuthLayout>
      <Wrapper>
        <Helmet title={t("Recuperar contraseña")} />

        <Typography component="h1" variant="h4" align="center" gutterBottom>
          {t("Recupere el acceso a su cuenta")}
        </Typography>
        <Typography component="h2" variant="body1" align="center">
          {t(
            "Introduzca su correo electrónico. Si existe una cuenta asociada, recibirá las instrucciones para cambiar la contraseña."
          )}
        </Typography>

        {requestSent && (
          <Alert mt={3} mb={2} severity="success" role="status">
            {t(
              "Solicitud enviada. Revise su bandeja de entrada y la carpeta de correo no deseado."
            )}
          </Alert>
        )}

        <Formik
          initialValues={{
            email: "",
            submit: false,
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string()
              .email(t("Introduzca un correo electrónico válido"))
              .max(255)
              .required(t("El correo electrónico es obligatorio")),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              await dispatch(
                resetPassword({
                  email: values.email,
                })
              );
              setRequestSent(true);
              setStatus({ success: true });
              setSubmitting(false);
            } catch (error) {
              const message = getApiErrorMessage(
                error,
                t("No pudimos enviar la solicitud.")
              );

              setStatus({ success: false });
              setErrors({ submit: message });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              {errors.submit && (
                <Alert mt={2} mb={1} severity="warning">
                  {errors.submit}
                </Alert>
              )}
              <TextField
                type="email"
                name="email"
                label={t("Correo electrónico")}
                value={values.email}
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                onBlur={handleBlur}
                onChange={handleChange}
                my={3}
                variant="outlined"
                autoComplete="email"
                disabled={isSubmitting}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <React.Fragment>
                    <CircularProgress
                      size={20}
                      color="inherit"
                      style={{ marginRight: 10 }}
                    />
                    {t("Enviando...")}
                  </React.Fragment>
                ) : (
                  t("Enviar instrucciones")
                )}
              </Button>
              <Button
                component={Link}
                to="/auth/sign-in"
                fullWidth
                style={{ marginTop: 12, textTransform: "none" }}
              >
                {t("Volver al inicio de sesión")}
              </Button>
            </form>
          )}
        </Formik>
      </Wrapper>
    </AuthLayout>
  );
}

export default ResetPassword;
